// breadcrumb.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BreadcrumbModel } from '../model/breadcrumb.model';
import { Router } from '@angular/router';

export interface BreadcrumbContext {
  isCustomerBased: boolean;
  customerId?: string;
  customerName?: string;
}

/**
 * Enhanced Breadcrumb Service with Context-Aware Support
 * 
 * This service provides dynamic breadcrumb generation based on navigation context.
 * It automatically detects whether a component is accessed independently or through
 * a customer view and adjusts breadcrumbs accordingly.
 * 
 * Usage Examples:
 * 
 * 1. For child components (like DeviceList, ThingList):
 *    ```typescript
 *    ngOnInit() {
 *      this.breadcrumbService.setContextAwareBreadcrumbs(
 *        DEVICE_BREADCRUMB,    // Default breadcrumbs for independent access
 *        'Devices',            // Component display name
 *        '/things/deviceList'  // Component URL (optional)
 *      );
 *    }
 *    ```
 * 
 * 2. For parent components that provide customer context:
 *    ```typescript
 *    ngOnInit() {
 *      const customerName = 'ACME Corp'; // Get from API or localStorage
 *      this.breadcrumbService.updateCustomerContext(this.customerId, customerName);
 *    }
 *    ```
 * 
 * Automatic Breadcrumb Patterns:
 * 
 * Independent access: Things > Devices
 * Customer context:   Management > Customers > Customer View > Devices
 * 
 */
@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private breadcrumbSubject = new BehaviorSubject<BreadcrumbModel[]>([]);
  breadcrumbs$ = this.breadcrumbSubject.asObservable();

  constructor(private router: Router) {}

  setBreadcrumbs(breadcrumbs: BreadcrumbModel[]) {
    this.breadcrumbSubject.next(breadcrumbs);
  }

  extendBreadcrumbs(newBreadcrumb: BreadcrumbModel) {
    const currentBreadcrumbs = this.getBreadcrumbs();
    // Remove any existing breadcrumb with the same id to avoid duplicates
    const filteredBreadcrumbs = currentBreadcrumbs.filter(b => b.id !== newBreadcrumb.id);
    this.breadcrumbSubject.next([...filteredBreadcrumbs, newBreadcrumb]);
  }

  removeBreadcrumb(id: string) {
    const currentBreadcrumbs = this.getBreadcrumbs();
    const filteredBreadcrumbs = currentBreadcrumbs.filter(b => b.id !== id);
    this.breadcrumbSubject.next(filteredBreadcrumbs);
  }

  getBreadcrumbs(): BreadcrumbModel[] {
    return this.breadcrumbSubject.getValue();
  }

  clearBreadcrumbs(): void {
    this.breadcrumbSubject.next([]);
  }

  /**
   * Detects the navigation context to determine if we're in customer-based view
   */
  detectNavigationContext(): BreadcrumbContext {
    const currentUrl = this.router.url;
    const isCustomerBased = currentUrl.includes('/customers/customerView/');
    
    let customerId: string | undefined;
    let customerName: string | undefined;
    
    if (isCustomerBased) {
      // Extract customer ID from URL like /customers/customerView/12345
      const match = currentUrl.match(/\/customers\/customerView\/([^/?]+)/);
      if (match) {
        customerId = match[1];
        // You can optionally get customer name from localStorage or service
        customerName = localStorage.getItem('currentCustomerName') || 'Customer';
      }
    }
    
    return {
      isCustomerBased,
      customerId,
      customerName
    };
  }

  /**
   * Sets context-aware breadcrumbs for child components
   * @param componentBreadcrumbs - Default breadcrumbs for independent access
   * @param componentName - Name of the current component (e.g., 'Devices', 'Things')
   * @param componentUrl - URL for the component in customer context
   */
  setContextAwareBreadcrumbs(
    componentBreadcrumbs: BreadcrumbModel[], 
    componentName: string,
    componentUrl?: string
  ) {
    const context = this.detectNavigationContext();
    
    if (context.isCustomerBased && context.customerId) {
      // Customer-based breadcrumbs: Management > Customers > Component
      const customerBreadcrumbs: BreadcrumbModel[] = [
        {
          id: 'management',
          name: 'navigation.management.managementLabel',
          url: ''
        },
        {
          id: 'customers',
          name: 'Customers',
          url: '/customers/customerList',
          key: 'customer-list'
        },
        {
          id: componentName.toLowerCase(),
          name: componentName,
          url: componentUrl || '#',
          key: `customer-${componentName.toLowerCase()}`
        }
      ];
      
      this.setBreadcrumbs(customerBreadcrumbs);
    } else {
      // Independent access: use provided breadcrumbs
      this.setBreadcrumbs(componentBreadcrumbs);
    }
  }

  /**
   * Updates breadcrumb with customer context information
   * @param customerId - Customer ID
   * @param customerName - Customer Name (optional)
   */
  updateCustomerContext(customerId: string, customerName?: string) {
    if (customerName) {
      localStorage.setItem('currentCustomerName', customerName);
    }
    
    const currentBreadcrumbs = this.getBreadcrumbs();
    const updatedBreadcrumbs = currentBreadcrumbs.map(breadcrumb => {
      if (breadcrumb.id === 'customer_view') {
        return {
          ...breadcrumb,
          name: customerName || breadcrumb.name,
          url: `/customers/customerView/${customerId}`
        };
      }
      return breadcrumb;
    });
    
    this.setBreadcrumbs(updatedBreadcrumbs);
  }
}
